/**
 * @file RGPD.tsx
 * @summary Page légale
 *
 * Page de mentions légales / RGPD / CGU / cookies. Conformité Privacy by design — cookies essentiels uniquement.
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import LegalPage from "@/components/LegalPage";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";

const summaryCards = [
  { title: "Qui traite ?", content: "MOJO SARL — CrossFit Chistera" },
  { title: "Pourquoi ?", content: "Répondre, organiser, gérer les adhésions" },
  { title: "Quelles données ?", content: "Identité, contact, message, créneau" },
  { title: "Avec qui ?", content: "Coachs, OVH, ResaWOD" },
  { title: "Combien de temps ?", content: "12 mois max (hors obligations légales)" },
  { title: "Vos droits", content: "Accès, rectification, effacement, opposition…" },
];

export default function RGPD() {
  return (
    <LegalPage
      title="Politique RGPD"
      subtitle="Protection de vos données personnelles"
      canonicalPath="/legal/rgpd"
      seoTitle="Politique RGPD & données personnelles — CrossFit Chistera"
      seoDescription="Politique RGPD CrossFit Chistera : données collectées via le formulaire de contact, finalités, durée de conservation (12 mois max), droits d'accès et de rectification, contact DPO."
      remember={[
        "Données : contact et réservation uniquement",
        "Aucun cookie publicitaire",
        "Conservation : 12 mois maximum",
        "Contact RGPD : contact@crossfitchistera.com",
        "Vous pouvez saisir la CNIL en cas de difficulté",
      ]}
    >
      {/* Summary cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 not-prose">
        {summaryCards.map((c) => (
          <div key={c.title} className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs font-semibold text-gold uppercase tracking-wider mb-1">{c.title}</p>
            <p className="text-sm text-foreground">{c.content}</p>
          </div>
        ))}
      </div>

      <h2>1. Responsable du traitement</h2>
      <p>MOJO (SARL) exploitant CrossFit Chistera, 37 rue Pierre et Marie Curie, 69780 Saint-Pierre-de-Chandieu, RCS Lyon 922 456 108. Contact RGPD : <a href="mailto:contact@crossfitchistera.com">contact@crossfitchistera.com</a>.</p>

      <h2>2. Données traitées</h2>
      <p>Identité et contact (nom, prénom, email, téléphone), demande (objet, message, créneau), suivi (historique échanges), données techniques minimales (logs serveur), données opérationnelles (ResaWOD). Nous ne demandons pas de données de santé via le formulaire.</p>

      <h2>3. Finalités et bases légales</h2>
      <ul>
        <li>Répondre à vos demandes : intérêt légitime</li>
        <li>Organiser séance d'essai / drop-in : mesures précontractuelles</li>
        <li>Gérer adhésion et réservations : exécution du contrat</li>
        <li>Facturation et comptabilité : obligation légale</li>
        <li>Sécurité du site : intérêt légitime</li>
        <li>Photos/vidéos : consentement ou intérêt légitime selon le contexte</li>
      </ul>

      <h2>4. Destinataires</h2>
      <p>Accès interne limité aux coachs et personnes habilitées. Sous-traitants : OVHcloud (hébergement), ResaWOD (gestion), et prestataires strictement nécessaires. Autres : expert-comptable, banque, autorités habilitées.</p>

      <h2>5. Transferts hors UE</h2>
      <p>Aucun transfert hors UE organisé dans le cadre standard du site.</p>

      <h2>6. Durées de conservation</h2>
      <ul>
        <li>Demandes via formulaire : 12 mois après clôture</li>
        <li>Séance d'essai / drop-in : 12 mois après la séance</li>
        <li>Données d'adhésion : durée du contrat + archivage légal</li>
        <li>Logs techniques : durée courte, limitée à la sécurité</li>
      </ul>

      <h2>7. Photos et vidéos (droit à l'image)</h2>
      <p>Des photos et vidéos peuvent être prises et diffusées sur le site et les réseaux sociaux. Nous privilégions les contenus d'ambiance. Pour un retrait, écrivez à <a href="mailto:contact@crossfitchistera.com">contact@crossfitchistera.com</a>.</p>
      <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg my-4">
        <p className="text-sm text-orange-800"><strong>Attention :</strong> Les plateformes de réseaux sociaux disposent de leurs propres règles de diffusion. Nous ne maîtrisons pas l'exploitation par les utilisateurs via les fonctions de partage.</p>
      </div>

      <h2>8. Vos droits</h2>
      <p>Accès, rectification, effacement, limitation, opposition, portabilité, retrait du consentement (images). Pour exercer vos droits : <a href="mailto:contact@crossfitchistera.com">contact@crossfitchistera.com</a>. En cas de difficulté, vous pouvez saisir la CNIL.</p>

      {/* CTA exercer droits */}
      <div className="bg-card border border-border rounded-lg p-6 my-8 text-center not-prose">
        <h3 className="font-heading text-xl text-foreground uppercase mb-3">Exercer vos droits</h3>
        <p className="text-sm text-muted-foreground mb-4">Précisez votre nom, l'objet de la demande, et si applicable un lien/capture pour le droit à l'image.</p>
        <a href="mailto:contact@crossfitchistera.com?subject=Exercice%20de%20droits%20RGPD">
          <Button className="bg-gold text-gold-foreground hover:bg-gold/90 rounded-lg font-semibold">
            <Mail size={16} className="mr-2" /> Écrire à contact@crossfitchistera.com
          </Button>
        </a>
      </div>

      <h2>9. Sécurité</h2>
      <p>Mesures adaptées : accès restreints, mesures anti-abus, hébergement professionnel, sauvegardes, gestion d'habilitations.</p>

      <h2>10. Cookies et traceurs</h2>
      <p>Aucun cookie publicitaire ni de mesure d'audience. Uniquement des traceurs techniques indispensables au fonctionnement, sans finalité de profilage.</p>

      <h2>11. Mise à jour</h2>
      <p>Cette politique peut être mise à jour pour refléter des évolutions. La version applicable est celle publiée au moment de la navigation.</p>

      <p>Voir aussi : <a href="/legal/mentions">Mentions légales</a> · <a href="/legal/cgu">CGU</a> · <a href="/legal/cgv">CGV</a> · <a href="/lexique">Lexique</a></p>
    </LegalPage>
  );
}
